import React, { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Sidebar from './components/Sidebar'
import Header from './components/Header'
import Dashboard from './pages/Dashboard'
import Sessions from './pages/Sessions'
import Hunts from './pages/Hunts'
import Certificates from './pages/Certificates'
import CertificateViewer from './pages/CertificateViewer'
import Prizes from './pages/Prizes'
import PrizeManagement from './pages/PrizeManagement'
import Analytics from './pages/Analytics'
import Settings from './pages/Settings'
import Login from './pages/Login'
import BookingAPIs from './pages/BookingAPIs';
import NotFound from './pages/NotFound';
import { AuthProvider, useAuth } from './hooks/useAuth.jsx'
import { Toaster } from 'react-hot-toast'
import './styles/email-theme.css'

// Create a context for mobile sidebar state
const MobileContext = React.createContext()

function AppContent() {
  const { isAuthenticated, loading } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Store the intended destination for after login
    const currentPath = window.location.pathname
    if (currentPath !== '/login') {
      sessionStorage.setItem('redirectAfterLogin', currentPath)
    }
    return <Login />
  }

  // Handle any unexpected errors by redirecting to login
  if (window.location.pathname === '/404' || window.location.pathname.includes('404')) {
    window.location.href = '/login'
    return null
  }


  return (
    <MobileContext.Provider value={{ sidebarOpen, setSidebarOpen }}>
      <div className="flex h-screen bg-gray-50">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <Sidebar onClose={() => setSidebarOpen(false)} />
        </div>
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header onMenuClick={() => setSidebarOpen(true)} />
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-4 lg:p-6">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/sessions" element={<Sessions />} />
              <Route path="/hunts" element={<Hunts />} />
              <Route path="/certificates" element={<Certificates />} />
              <Route path="/certificates/view/:token" element={<CertificateViewer />} />
              <Route path="/prizes" element={<Prizes />} />
              <Route path="/prize-management" element={<PrizeManagement />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/:tab" element={<Settings />} />
              <Route path="/booking-apis" element={<BookingAPIs />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
      </div>
    </MobileContext.Provider>
  )
}

function App() {
  // Global error handler for unhandled errors
  useEffect(() => {
    const handleError = (error) => {
      console.error('Global error:', error)
      // Only redirect on specific network errors, not React errors
      if (error.message?.includes('Failed to fetch') || error.message?.includes('Network Error')) {
        console.log('Network error detected, but not redirecting to avoid loops')
      }
    }

    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason)
      // Don't redirect on promise rejections to avoid loops
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
